import { LegalLayout } from './LegalLayout';

const LAST_UPDATED = '2026-10-04';

// Draft prepared by engineering from the current codebase behaviour. It has
// not been reviewed by the owner or by counsel — do not treat it as final
// before that review happens and this comment is removed.
export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated={LAST_UPDATED}>
      <section>
        <p>
          FewerBags is a household meal-planning service operated by{' '}
          <strong>Five01 Creations</strong> (&ldquo;we&rdquo;, &ldquo;us&rdquo;) at{' '}
          <a href="https://fewerbags.com">fewerbags.com</a> (fewerbags.app redirects to the same
          service). This policy explains what we collect through the web app and companion mobile
          apps, why, and how you can control or remove it. Questions or requests go to{' '}
          <a href="mailto:support@five01creations.com">support@five01creations.com</a>.
        </p>
      </section>

      <section>
        <h2>What we collect</h2>
        <p>
          <strong>Account data.</strong> Your email address, display name, and a password hash (we
          store a salted argon2 hash, never your password itself) — or, if you set one up instead, a
          passkey credential. We use your email to verify your address and to send password-reset
          messages; we do not send marketing email.
        </p>
        <p>
          <strong>Household content.</strong> Everything you and your household enter to use the
          app: recipes and their ingredients/steps, meal plans and the cooking timeline, pantry
          items, grocery lists and check-offs, purchase records including prices you record, and any
          photos you upload (recipe photos, ingredient photos). Photos are re-encoded on upload and
          have EXIF/GPS metadata stripped before they are stored, so a photo you add does not carry
          your camera's location data.
        </p>
        <p>
          <strong>Sessions and devices.</strong> A single sign-in cookie identifies your session; we
          also record the device name you or your browser reports and when a session was last used,
          so the Devices page in Settings can show you where you're signed in and let you sign a
          device out remotely.
        </p>
        <p>
          <strong>Location, on demand only.</strong> The app never reads your device's location on
          its own. When you tap &ldquo;Detect store&rdquo; while shopping, or use &ldquo;use my
          location&rdquo; when adding a new store, your browser asks you for permission and, if
          granted, sends your coordinates to our server for a one-off nearest-store lookup or to
          record the store's location. We only store coordinates for stores you (or another
          household) explicitly create — we do not keep a history of everywhere you have been.
        </p>
      </section>

      <section>
        <h2>The shared catalog</h2>
        <p>
          Some information you contribute is useful to every household using FewerBags, not just
          yours: ingredient names and photos, product listings, store details, and the prices and
          shelf locations you observe while shopping. We store these in a shared catalog, but we
          only ever show <em>aggregated</em> figures (for example, a median recent price or the most
          commonly reported aisle) — we never show another household which household contributed a
          given price, location, or shelf-life observation. If an administrator disables a
          household's account, that household's past contributions are excluded from the shared
          aggregates going forward.
        </p>
      </section>

      <section>
        <h2>Importing a recipe from a URL or a photo</h2>
        <p>
          When you paste a recipe URL to import it, our server fetches that page on your behalf
          (with safeguards against fetching internal/private network addresses) to read its content.
          We store the source URL with the recipe so it stays attributed to the site you imported it
          from. When you approve the import, we also download that page's recipe photo, if it has
          one, and store it privately as your household's photo for the recipe.
        </p>
        <p>
          When you import a recipe from a photo, the photo (with EXIF/GPS metadata already stripped)
          is sent to our AI provider, Anthropic, whose Claude model reads the recipe text from it.
        </p>
      </section>

      <section>
        <h2>Cookies and tracking</h2>
        <p>
          The web app sets one cookie — a signed, <code>httpOnly</code> session cookie sent only
          over HTTPS and only to fewerbags.com — to keep you signed in. We do not use advertising
          cookies, third-party analytics, or any tracking SDKs, and we do not sell or share your
          data with advertisers. The companion mobile apps do not include any analytics SDK either.
        </p>
      </section>

      <section>
        <h2>How long we keep things</h2>
        <p>
          Your content stays available for as long as your account and household exist. We take an
          encrypted backup of the database every night so we can recover from a failure; copies are
          kept on the server for 14 days and in off-site storage for up to 13 months, after which
          they are deleted automatically. Content you delete may therefore persist in those backups
          until they age out, but it is never restored into the live service except to recover from
          data loss.
        </p>
        <p>
          You can delete your account at any time from{' '}
          <strong>Settings → Profile → Delete my account</strong>. Deleting your account signs out
          every device, removes your passkeys and any pending email tokens, and removes your
          membership from every household. If you were the only member of a household, that
          household and everything in it is deleted with your account. If other people are still in
          a household you own, you'll be asked to hand ownership to another member first — their
          copy of that household's content is unaffected. Your account record itself is anonymised
          (your email and name are replaced) rather than immediately erased from the database, so
          that content other people still rely on (a shared household, a public recipe you
          contributed) doesn't disappear out from under them.
        </p>
      </section>

      <section>
        <h2>Changes to this policy</h2>
        <p>
          If we make a material change to this policy, we'll update the date at the top of this
          page. Continued use of the service after a change means you accept the updated policy.
        </p>
      </section>
    </LegalLayout>
  );
}
